import { supabase } from '@/lib/supabase';
import { getJSON, setJSON, removeJSON } from '@/utils/storage/jsonStorage';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';

const log = createScopedLogger('FAVORITES-CACHE');

interface FavoriteItem {
  id: string;
  content_id: number;
  content_type: 'serie' | 'episode';
  smartuser_id: string;
  created_at: string;
}

interface FavoritesCache {
  series: Set<number>;
  episodes: Set<number>;
  allFavorites: FavoriteItem[];
  lastSync: number;
  smartuserId: string;
}

const FAVORITES_CACHE_KEY = '@favorites_cache';
const CACHE_TTL = 1 * 60 * 1000; // 1 minute

class FavoritesCacheService {
  private cache: FavoritesCache | null = null;
  private initialized = false;
  private syncInProgress = false;

  async initialize(smartuserId?: string): Promise<void> {
    if (this.initialized) {
      // If already initialized but with different user, re-initialize
      if (smartuserId && this.cache && this.cache.smartuserId !== smartuserId) {
        log.info('User changed, re-initializing...');
        this.initialized = false;
        this.cache = null;
      } else {
        return;
      }
    }

    const startTime = Date.now();
    log.info('Initializing favorites cache...');

    try {
      // Try to load from AsyncStorage first
      const parsed = await getJSON<{
        series: number[];
        episodes: number[];
        allFavorites: FavoriteItem[];
        lastSync: number;
        smartuserId: string;
      }>(FAVORITES_CACHE_KEY);
      if (parsed) {
        this.cache = {
          series: new Set(parsed.series),
          episodes: new Set(parsed.episodes),
          allFavorites: parsed.allFavorites,
          lastSync: parsed.lastSync,
          smartuserId: parsed.smartuserId,
        };

        const age = Date.now() - this.cache.lastSync;
        log.info(`Loaded from storage (age: ${Math.round(age / 1000)}s)`);

        // If cache is stale or for different user, trigger background sync
        if (age > CACHE_TTL || (smartuserId && this.cache.smartuserId !== smartuserId)) {
          log.info('Cache stale or user changed, syncing in background...');
          this.syncFromDatabase(smartuserId).catch(err =>
            log.error('Background sync error:', err)
          );
        }
      } else if (smartuserId) {
        // No cache, load from database
        await this.syncFromDatabase(smartuserId);
      }

      this.initialized = true;
      const duration = Date.now() - startTime;
      log.info(`Initialization complete in ${duration}ms`);
    } catch (error) {
      log.error('Error initializing:', error);
      this.cache = null;
      this.initialized = true;
    }
  }

  async syncFromDatabase(smartuserId?: string): Promise<void> {
    if (!supabase || this.syncInProgress) return;

    if (!smartuserId && !this.cache?.smartuserId) {
      log.warn('No user ID available for sync');
      return;
    }

    const userId = smartuserId || this.cache!.smartuserId;
    this.syncInProgress = true;

    const startTime = Date.now();
    log.info(`Syncing from database for user: ${userId}...`);

    try {
      const { data, error, ok } = await withSupabaseResult<FavoriteItem[]>(
        supabase
          .from('user_favorites')
          .select('*')
          .eq('smartuser_id', userId)
          .order('created_at', { ascending: false }),
        { operation: 'favoritesCache.syncFromDatabase', logger: log, fallback: [] }
      );

      if (!ok) {
        log.error('Error syncing from database:', error?.message);
        return;
      }

      const favorites = data || [];
      const seriesSet = new Set<number>();
      const episodesSet = new Set<number>();

      favorites.forEach(fav => {
        if (fav.content_type === 'serie') {
          seriesSet.add(fav.content_id);
        } else if (fav.content_type === 'episode') {
          episodesSet.add(fav.content_id);
        }
      });

      this.cache = {
        series: seriesSet,
        episodes: episodesSet,
        allFavorites: favorites,
        lastSync: Date.now(),
        smartuserId: userId,
      };

      // Persist to AsyncStorage
      await this.persistCache();

      const duration = Date.now() - startTime;
      log.info(
        `Sync complete: ${seriesSet.size} series, ${episodesSet.size} episodes (${duration}ms)`
      );
    } catch (error) {
      log.error('Unexpected error during sync:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  private async persistCache(): Promise<void> {
    if (!this.cache) return;

    const toStore = {
      series: [...this.cache.series],
      episodes: [...this.cache.episodes],
      allFavorites: this.cache.allFavorites,
      lastSync: this.cache.lastSync,
      smartuserId: this.cache.smartuserId,
    };

    await setJSON(FAVORITES_CACHE_KEY, toStore);
  }

  async isFavorite(contentId: number, contentType: 'serie' | 'episode'): Promise<boolean> {
    await this.initialize();

    if (!this.cache) {
      log.warn('Cache not available, returning false');
      return false;
    }

    const isFav = contentType === 'serie'
      ? this.cache.series.has(contentId)
      : this.cache.episodes.has(contentId);

    log.debug(`Check ${contentType} ${contentId}: ${isFav ? 'IS FAVORITE' : 'NOT FAVORITE'}`);
    return isFav;
  }

  async addFavorite(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<{ success: boolean; error?: any }> {
    log.info(`Adding ${contentType} ${contentId} to favorites...`);

    try {
      const { data, error, ok } = await withSupabaseResult<FavoriteItem>(
        supabase!
          .from('user_favorites')
          .upsert(
            {
              smartuser_id: smartuserId,
              content_id: contentId,
              content_type: contentType,
            },
            { onConflict: 'smartuser_id,content_id,content_type' }
          )
          .select()
          .single(),
        { operation: 'favoritesCache.addFavorite', logger: log }
      );

      if (!ok) {
        log.error('Error adding to database:', error?.message);
        return { success: false, error };
      }

      // Update cache
      await this.initialize(smartuserId);
      if (this.cache) {
        if (contentType === 'serie') {
          this.cache.series.add(contentId);
        } else {
          this.cache.episodes.add(contentId);
        }

        if (data) {
          this.cache.allFavorites.unshift(data);
        }

        this.cache.lastSync = Date.now();
        await this.persistCache();
      }

      log.info(`Added ${contentType} ${contentId} successfully`);
      return { success: true };
    } catch (error) {
      log.error('Unexpected error adding favorite:', error);
      return { success: false, error };
    }
  }

  async removeFavorite(
    smartuserId: string,
    contentId: number,
    contentType: 'serie' | 'episode'
  ): Promise<{ success: boolean; error?: any }> {
    log.info(`Removing ${contentType} ${contentId} from favorites...`);

    try {
      // Remove from database first
      const { error, ok } = await withSupabaseResult(
        supabase!
          .from('user_favorites')
          .delete()
          .eq('smartuser_id', smartuserId)
          .eq('content_id', contentId)
          .eq('content_type', contentType),
        { operation: 'favoritesCache.removeFavorite', logger: log }
      );

      if (!ok) {
        log.error('Error removing from database:', error?.message);
        return { success: false, error };
      }

      // Update cache
      await this.initialize(smartuserId);
      if (this.cache) {
        if (contentType === 'serie') {
          this.cache.series.delete(contentId);
        } else {
          this.cache.episodes.delete(contentId);
        }

        this.cache.allFavorites = this.cache.allFavorites.filter(
          fav => !(fav.content_id === contentId && fav.content_type === contentType)
        );

        this.cache.lastSync = Date.now();
        await this.persistCache();
      }

      log.info(`Removed ${contentType} ${contentId} successfully`);
      return { success: true };
    } catch (error) {
      log.error('Unexpected error removing favorite:', error);
      return { success: false, error };
    }
  }

  async getAllFavorites(): Promise<FavoriteItem[]> {
    await this.initialize();

    if (!this.cache) {
      return [];
    }

    return this.cache.allFavorites;
  }

  async forceSync(smartuserId: string): Promise<void> {
    log.info('Force sync requested');
    await this.syncFromDatabase(smartuserId);
  }

  async clear(): Promise<void> {
    log.info('Clearing cache');
    this.cache = null;
    this.initialized = false;
    await removeJSON(FAVORITES_CACHE_KEY);
  }

  getCacheStats(): { seriesCount: number; episodesCount: number; lastSync: string } | null {
    if (!this.cache) {
      return null;
    }

    return {
      seriesCount: this.cache.series.size,
      episodesCount: this.cache.episodes.size,
      lastSync: new Date(this.cache.lastSync).toISOString(),
    };
  }
}

export const favoritesCacheService = new FavoritesCacheService();
export type { FavoriteItem };
